import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ label, value, sub }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
      <p className="text-slate-400 text-xs uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-white mt-1">{value}</p>
      {sub && <p className="text-slate-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function ProspectCard({ prospect, onAnalyze, onGenerateEmail, onGenerateScript, onStartSequence, onRecordConsent, onApproveCall, onMakeCall, onGeneratePresentation, onGenerateQuote, onScheduleMeeting, loading }) {
  const analysis = prospect.ai_analysis || {};
  const score = prospect.compatibility_score || analysis.compatibility_score || 0;
  const scoreColor = score >= 80 ? 'text-green-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400';
  const hasConsent = prospect.consent_given;
  const hasPhone = prospect.phone || prospect.contact_phone;

  return (
    <div data-testid={`prospect-${prospect.id}`} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-3">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-white font-semibold">{prospect.company || 'Sin nombre'}</h3>
          <p className="text-slate-400 text-sm">{prospect.industry} {prospect.city ? `| ${prospect.city}, ${prospect.state}` : ''}</p>
          {prospect.contact_name && <p className="text-slate-500 text-xs mt-1">{prospect.contact_name} {prospect.contact_title ? `- ${prospect.contact_title}` : ''}</p>}
          {prospect.email && <p className="text-blue-400 text-xs">{prospect.email}</p>}
        </div>
        <div className="text-right">
          <span className={`text-lg font-bold ${scoreColor}`}>{score}%</span>
          <p className="text-slate-500 text-xs">{prospect.status}</p>
          {prospect.temperature && <span className={`text-xs px-2 py-0.5 rounded ${prospect.temperature === 'warm' ? 'bg-orange-900/50 text-orange-300' : 'bg-blue-900/50 text-blue-300'}`}>{prospect.temperature}</span>}
        </div>
      </div>

      {analysis.recommended_services && (
        <div className="mt-2 flex flex-wrap gap-1">
          {(Array.isArray(analysis.recommended_services) ? analysis.recommended_services : []).map((s, i) => (
            <span key={i} className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{s}</span>
          ))}
        </div>
      )}

      {analysis.approach_strategy && (
        <p className="text-slate-400 text-xs mt-2 italic">"{analysis.approach_strategy.substring(0, 150)}..."</p>
      )}

      {analysis.estimated_project_value && (
        <p className="text-green-400 text-xs mt-1 font-semibold">Valor estimado: {analysis.estimated_project_value}</p>
      )}

      <div className="flex gap-2 mt-3 flex-wrap">
        {!prospect.ai_analysis && (
          <Button data-testid={`analyze-${prospect.id}`} size="sm" variant="outline" onClick={() => onAnalyze(prospect.id)} disabled={loading} className="text-xs">
            Analizar con IA
          </Button>
        )}
        {prospect.ai_analysis && (
          <>
            <Button data-testid={`email-${prospect.id}`} size="sm" variant="outline" onClick={() => onGenerateEmail(prospect.id)} disabled={loading} className="text-xs">
              Generar Email
            </Button>
            <Button data-testid={`script-${prospect.id}`} size="sm" variant="outline" onClick={() => onGenerateScript(prospect.id)} disabled={loading} className="text-xs">
              Script Llamada
            </Button>
            {prospect.email && !prospect.sales_sequence_active && (
              <Button data-testid={`sequence-${prospect.id}`} size="sm" onClick={() => onStartSequence(prospect.id)} disabled={loading} className="text-xs bg-green-600 hover:bg-green-700">
                Iniciar Secuencia
              </Button>
            )}
            {prospect.sales_sequence_active && (
              <span className="text-xs bg-green-900/50 text-green-300 px-2 py-1 rounded flex items-center">Secuencia: {prospect.sales_sequence_stage}</span>
            )}
          </>
        )}
        {/* Consent & Call Controls */}
        {!hasConsent && !prospect.consent_revoked && (
          <Button size="sm" variant="outline" onClick={() => onRecordConsent(prospect.id, 'email_reply')} disabled={loading} className="text-xs border-yellow-600 text-yellow-400">
            Registrar Consentimiento
          </Button>
        )}
        {hasConsent && <span className="text-xs bg-green-900/40 text-green-300 px-2 py-0.5 rounded">Consentimiento OK</span>}
        {prospect.consent_revoked && <span className="text-xs bg-red-900/40 text-red-300 px-2 py-0.5 rounded">Opt-out</span>}
        {hasConsent && hasPhone && !prospect.call_approved && (
          <Button size="sm" variant="outline" onClick={() => onApproveCall(prospect.id)} disabled={loading} className="text-xs border-blue-600 text-blue-400">
            Aprobar Llamada
          </Button>
        )}
        {prospect.call_approved && (
          <Button size="sm" onClick={() => onMakeCall(prospect.id)} disabled={loading} className="text-xs bg-blue-600 hover:bg-blue-700">
            Llamar con Retell
          </Button>
        )}
        {prospect.ai_analysis && (
          <Button size="sm" variant="outline" onClick={() => onGeneratePresentation(prospect.id)} disabled={loading} className="text-xs border-purple-600 text-purple-400">
            Presentacion
          </Button>
        )}
        {prospect.ai_analysis && (
          <Button size="sm" variant="outline" onClick={() => onGenerateQuote(prospect.id)} disabled={loading} className="text-xs border-teal-600 text-teal-400">
            Cotizacion
          </Button>
        )}
        {prospect.ai_analysis && (
          <Button size="sm" variant="outline" onClick={() => onScheduleMeeting(prospect.id)} disabled={loading} className="text-xs border-cyan-600 text-cyan-400">
            Agendar Cita
          </Button>
        )}
      </div>
    </div>
  );
}

export default function SalesAgentPage() {
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('prospects');
  const [emailPreview, setEmailPreview] = useState(null);
  const [scriptPreview, setScriptPreview] = useState(null);
  const [message, setMessage] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, prospectsRes, compRes] = await Promise.all([
        fetch(`${API}/api/sales-agent/stats`, { credentials: 'include' }),
        fetch(`${API}/api/b2b/prospects`, { credentials: 'include' }),
        fetch(`${API}/api/sales-agent/compliance-status`, { credentials: 'include' })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (prospectsRes.ok) {
        const data = await prospectsRes.json();
        setProspects(Array.isArray(data) ? data : data.prospects || []);
      }
      if (compRes.ok) setCompliance(await compRes.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showMsg = (text, ms = 4000) => { setMessage(text); setTimeout(() => setMessage(''), ms); };

  const handleAnalyze = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/analyze-prospect/${id}`, { method: 'POST', credentials: 'include' });
      if (res.ok) { showMsg('Prospecto analizado'); fetchData(); }
      else showMsg('Error al analizar');
    } catch (e) { showMsg('Error de conexion'); }
    setLoading(false);
  };

  const handleGenerateEmail = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/generate-email/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: 'cold_intro', language: 'en' })
      });
      if (res.ok) {
        const data = await res.json();
        setEmailPreview({ ...data, prospect_id: id });
        setActiveTab('email');
      }
    } catch (e) { showMsg('Error generando email'); }
    setLoading(false);
  };

  const handleGenerateScript = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/generate-call-script/${id}`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setScriptPreview({ ...data, prospect_id: id });
        setActiveTab('script');
      }
    } catch (e) { showMsg('Error generando script'); }
    setLoading(false);
  };

  const handleStartSequence = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/start-sequence/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: 'en' })
      });
      if (res.ok) {
        const data = await res.json();
        showMsg(data.email_sent ? `Secuencia iniciada - Email enviado a ${data.email_to}` : 'Secuencia iniciada');
        fetchData();
      } else {
        const err = await res.json();
        showMsg(err.detail || 'Error al iniciar secuencia');
      }
    } catch (e) { showMsg('Error de conexion'); }
    setLoading(false);
  };

  const handleBatchAnalyze = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/batch-analyze`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 10, min_score: 60 })
      });
      if (res.ok) {
        const data = await res.json();
        showMsg(`${data.prospects_created} prospectos creados de ${data.analyzed} discoveries`);
        fetchData();
      }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleRecordConsent = async (id, method) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/record-consent/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method, notes: '' })
      });
      if (res.ok) { showMsg('Consentimiento registrado'); fetchData(); }
      else { const err = await res.json(); showMsg(err.detail || 'Error'); }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleApproveCall = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/approve-call/${id}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}'
      });
      if (res.ok) { showMsg('Llamada aprobada'); fetchData(); }
      else { const err = await res.json(); showMsg(err.detail || 'Error'); }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleMakeCall = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/make-call/${id}`, { method: 'POST', credentials: 'include' });
      if (res.ok) { showMsg('Llamada iniciada'); fetchData(); }
      else { const err = await res.json(); showMsg(err.detail || 'Error'); }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleProcessSequences = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/process-sequences`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        showMsg(`Procesado: ${data.emails_sent} emails enviados, ${data.sequences_completed} completadas`);
        fetchData();
      }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleGeneratePresentation = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/generate-presentation/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: 'en' })
      });
      if (res.ok) { showMsg('Presentacion generada. Use "Generar Email" para enviarla.'); fetchData(); }
      else { const err = await res.json(); showMsg(err.detail || 'Error'); }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleGenerateQuote = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/generate-quote/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        showMsg(`Cotizacion ${data.quote_number} generada - $${data.total?.toLocaleString()} (${data.items} items). Pendiente aprobacion.`);
        fetchData();
      } else { const err = await res.json(); showMsg(err.detail || 'Error'); }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleScheduleMeeting = async (id) => {
    const date = prompt('Fecha de la cita (YYYY-MM-DD):');
    if (!date) return;
    const time = prompt('Hora (ej: 10:00 AM):');
    if (!time) return;
    const type = prompt('Tipo (site_evaluation / video_call / phone_call):') || 'site_evaluation';
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/schedule-meeting/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, time, type })
      });
      if (res.ok) { showMsg('Cita agendada'); fetchData(); }
      else { const err = await res.json(); showMsg(err.detail || 'Error'); }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleSendObservations = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/send-observations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ min_score: 65 })
      });
      if (res.ok) { const data = await res.json(); showMsg(data.message); }
      else { const err = await res.json(); showMsg(err.detail || 'Error'); }
    } catch (e) { showMsg('Error'); }
    setLoading(false);
  };

  const handleSendEmail = async () => {
    if (!emailPreview) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales-agent/send-email/${emailPreview.prospect_id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: emailPreview.subject, body: emailPreview.body, stage: emailPreview.stage })
      });
      if (res.ok) { showMsg('Email enviado'); setEmailPreview(null); setActiveTab('prospects'); fetchData(); }
      else { const err = await res.json(); showMsg(err.detail || 'Error al enviar'); }
    } catch (e) { showMsg('Error de conexion'); }
    setLoading(false);
  };

  const analyzed = prospects.filter(p => p.ai_analysis);
  const unanalyzed = prospects.filter(p => !p.ai_analysis && p.source === 'auto_discovery');

  return (
    <div data-testid="sales-agent-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Agente de Ventas IA</h1>
          <p className="text-slate-400 text-sm">Prospección automatizada B2B para IQES LLC</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="send-observations-btn" variant="outline" onClick={handleSendObservations} disabled={loading}>
            Enviar Observaciones
          </Button>
          <Button data-testid="process-sequences-btn" variant="outline" onClick={handleProcessSequences} disabled={loading}>
            Procesar Secuencias
          </Button>
          <Button data-testid="batch-analyze-btn" variant="outline" onClick={handleBatchAnalyze} disabled={loading}>
            Convertir Discoveries
          </Button>
        </div>
      </div>

      {message && (
        <div data-testid="agent-message" className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">
          {message}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3">
          <StatCard label="Prospectos" value={stats.total_prospects} />
          <StatCard label="Analizados" value={stats.analyzed_prospects} />
          <StatCard label="Secuencias" value={stats.active_sequences} sub="activas" />
          <StatCard label="Contactados Hoy" value={stats.contacted_today} />
          <StatCard label="Esta Semana" value={stats.contacted_this_week} />
          <StatCard label="Discoveries" value={stats.pending_discoveries} sub="pendientes" />
          <StatCard label="Bids" value={stats.total_bids_found} />
          <StatCard label="Llamadas" value={stats.calls_pending_approval} sub="aprobadas" />
          <StatCard label="Consentimiento" value={stats.prospects_with_consent} sub="opt-in" />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="prospects">Prospectos ({prospects.length})</TabsTrigger>
          <TabsTrigger value="email">Email Preview</TabsTrigger>
          <TabsTrigger value="script">Call Script</TabsTrigger>
          <TabsTrigger value="stages">Etapas de Venta</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        <TabsContent value="prospects" className="mt-4">
          {unanalyzed.length > 0 && (
            <div className="mb-4">
              <h3 className="text-slate-300 text-sm font-semibold mb-2">Sin Analizar ({unanalyzed.length})</h3>
              {unanalyzed.map(p => (
                <ProspectCard key={p.id} prospect={p} onAnalyze={handleAnalyze} onGenerateEmail={handleGenerateEmail} onGenerateScript={handleGenerateScript} onStartSequence={handleStartSequence} onRecordConsent={handleRecordConsent} onApproveCall={handleApproveCall} onMakeCall={handleMakeCall} onGeneratePresentation={handleGeneratePresentation} onGenerateQuote={handleGenerateQuote} onScheduleMeeting={handleScheduleMeeting} loading={loading} />
              ))}
            </div>
          )}
          {analyzed.length > 0 && (
            <div>
              <h3 className="text-slate-300 text-sm font-semibold mb-2">Analizados ({analyzed.length})</h3>
              {analyzed.sort((a, b) => (b.compatibility_score || 0) - (a.compatibility_score || 0)).map(p => (
                <ProspectCard key={p.id} prospect={p} onAnalyze={handleAnalyze} onGenerateEmail={handleGenerateEmail} onGenerateScript={handleGenerateScript} onStartSequence={handleStartSequence} onRecordConsent={handleRecordConsent} onApproveCall={handleApproveCall} onMakeCall={handleMakeCall} onGeneratePresentation={handleGeneratePresentation} onGenerateQuote={handleGenerateQuote} onScheduleMeeting={handleScheduleMeeting} loading={loading} />
              ))}
            </div>
          )}
          {prospects.filter(p => !p.ai_analysis && p.source !== 'auto_discovery').length > 0 && (
            <div className="mt-4">
              <h3 className="text-slate-300 text-sm font-semibold mb-2">Otros Prospectos</h3>
              {prospects.filter(p => !p.ai_analysis && p.source !== 'auto_discovery').map(p => (
                <ProspectCard key={p.id} prospect={p} onAnalyze={handleAnalyze} onGenerateEmail={handleGenerateEmail} onGenerateScript={handleGenerateScript} onStartSequence={handleStartSequence} onRecordConsent={handleRecordConsent} onApproveCall={handleApproveCall} onMakeCall={handleMakeCall} onGeneratePresentation={handleGeneratePresentation} onGenerateQuote={handleGenerateQuote} onScheduleMeeting={handleScheduleMeeting} loading={loading} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="email" className="mt-4">
          {emailPreview ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Email Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <label className="text-slate-400 text-xs">Asunto:</label>
                    <input data-testid="email-subject" className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1" value={emailPreview.subject} onChange={e => setEmailPreview({...emailPreview, subject: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Cuerpo:</label>
                    <textarea data-testid="email-body" className="w-full bg-slate-900 text-white border border-slate-600 rounded px-3 py-2 text-sm mt-1 min-h-[200px]" rows={10} value={emailPreview.body} onChange={e => setEmailPreview({...emailPreview, body: e.target.value})} />
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid="send-email-btn" onClick={handleSendEmail} disabled={loading} className="bg-green-600 hover:bg-green-700">
                      Enviar Email
                    </Button>
                    <Button variant="outline" onClick={() => { setEmailPreview(null); setActiveTab('prospects'); }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <p className="text-slate-500 text-sm">Seleccione un prospecto y haga click en "Generar Email"</p>
          )}
        </TabsContent>

        <TabsContent value="script" className="mt-4">
          {scriptPreview ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Script de Llamada</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {scriptPreview.opening && (
                  <div className="bg-blue-900/30 border border-blue-800 rounded p-3">
                    <p className="text-blue-300 text-xs font-semibold mb-1">APERTURA:</p>
                    <p className="text-white text-sm">{scriptPreview.opening}</p>
                  </div>
                )}
                {(scriptPreview.stages || []).map((stage, i) => (
                  <div key={i} className="bg-slate-900/50 border border-slate-700 rounded p-3">
                    <p className="text-yellow-400 text-xs font-semibold mb-1">{(i + 1)}. {stage.name?.toUpperCase()}</p>
                    <p className="text-white text-sm">{stage.script}</p>
                    {stage.if_positive && <p className="text-green-400 text-xs mt-2">Si positivo: {stage.if_positive}</p>}
                    {stage.if_negative && <p className="text-red-400 text-xs mt-1">Si negativo: {stage.if_negative}</p>}
                  </div>
                ))}
                {scriptPreview.objection_responses && (
                  <div className="bg-red-900/20 border border-red-800/50 rounded p-3">
                    <p className="text-red-300 text-xs font-semibold mb-2">MANEJO DE OBJECIONES:</p>
                    {Object.entries(scriptPreview.objection_responses).map(([key, val]) => (
                      <div key={key} className="mb-2">
                        <p className="text-red-400 text-xs font-semibold">"{key}"</p>
                        <p className="text-white text-xs">{typeof val === 'string' ? val : JSON.stringify(val)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <p className="text-slate-500 text-sm">Seleccione un prospecto analizado y haga click en "Script Llamada"</p>
          )}
        </TabsContent>

        <TabsContent value="stages" className="mt-4">
          <div className="space-y-3">
            {[1,2,3,4,5,6,7,8].map(num => {
              const stage = { "1": {name:"Presentacion",color:"bg-blue-600"}, "2": {name:"Calificacion",color:"bg-purple-600"}, "3": {name:"Propuesta de Valor",color:"bg-indigo-600"}, "4": {name:"Analisis de Necesidades",color:"bg-cyan-600"}, "5": {name:"Presentacion Solucion",color:"bg-teal-600"}, "6": {name:"Manejo Objeciones",color:"bg-orange-600"}, "7": {name:"Cierre",color:"bg-green-600"}, "8": {name:"Fin Conversacion",color:"bg-slate-600"} }[String(num)];
              return (
                <div key={num} className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className={`w-8 h-8 ${stage.color} rounded-full flex items-center justify-center text-white font-bold text-sm`}>{num}</div>
                  <div>
                    <p className="text-white font-semibold text-sm">{stage.name}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          {compliance && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Total" value={compliance.total_prospects} />
                <StatCard label="Con Consentimiento" value={compliance.with_consent} />
                <StatCard label="Opt-Out" value={compliance.consent_revoked} />
                <StatCard label="Secuencias Activas" value={compliance.active_sequences} />
                <StatCard label="Sin Consentimiento" value={compliance.contacted_without_consent} sub="contactados" />
              </div>
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader><CardTitle className="text-white text-lg">Reglas de Compliance (TCPA/FTSA Florida)</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {compliance.compliance_rules && Object.entries(compliance.compliance_rules).map(([key, val]) => (
                    <div key={key} className="bg-slate-900/50 border border-slate-700 rounded p-3">
                      <p className="text-yellow-400 text-xs font-semibold uppercase">{key.replace(/_/g, ' ')}</p>
                      <p className="text-white text-sm mt-1">{val}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
